import React, { useRef } from "react"
import { ThemeProvider } from "styled-components"

import componentThemeExtension from "utils/componentThemeExtension"
import cloudinaryImagePerDevice from "utils/cloudinaryImagePerDevice"

import BannerHero from "components/hero/banner"
import CenteredHero from "components/hero/centered"
import TextAndImage from "components/text-and-media/text-and-image"
import ContentfulButton from "./button"
import ContentfulImage from "./image"
import ContentfulVideo from "./video"
import ContentfulSignupForm from "./sign-up-form"

const ContentfulHero = ({ component, onTop, elRef }) => {
  const {
    layout,
    backgroundColor,
    preHeadingFont,
    preHeadingText,
    preHeadingLogo,
    headingFont,
    heading,
    subheading,
    backgroundMedia,
    minimumHeight,
    targetNavigationAnchor,
    countdownTimerEndDate,
    metrics,
    foregroundImage,
    foregroundVideo,
    button,
    secondaryButton,
    includeColorOverlay,
    signupForm,
    id,
    hideOnPageLoad,
  } = component

  const preText = preHeadingText
    ? preHeadingText.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
    : null
  const headText = heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")

  const preheadingProp = {
    text: preHeadingFont !== "mbg Monoline" ? preText : null,
    monolineText: preHeadingFont === "mbg Monoline" ? preText : null,
    logo: preHeadingLogo ? preHeadingLogo[0].secure_url : null,
    aspectLogo: preHeadingLogo ? preHeadingLogo[0].height / preHeadingLogo[0].width : 0,
    logoLeft: layout === "Split",
  }

  const headingProp = {
    text: headingFont !== "mbg Monoline" ? headText : null,
    monolineText: headingFont === "mbg Monoline" ? headText : null,
  }

  const backgroundImages =
    backgroundMedia?.length > 0 ? cloudinaryImagePerDevice(backgroundMedia) : null

  const formRef = useRef(null)
  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop)}>
      {["Banner", "Banner Slim"].includes(layout) && (
        <BannerHero
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={elRef}
          preheading={preheadingProp}
          heading={headingProp}
          subheading={subheading && subheading.childMarkdownRemark.html}
          variant={layout === "Banner Slim" ? "slim" : "regular"}
          backgroundImages={backgroundImages}
          minHeight={minimumHeight}
          buttonElement={button && <ContentfulButton component={button} />}
          secondaryButtonElement={
            secondaryButton && <ContentfulButton component={secondaryButton} />
          }
          hasOverlay={includeColorOverlay}
          signupForm={
            signupForm && <ContentfulSignupForm component={signupForm} elRef={formRef} nested />
          }
        />
      )}
      {["Centered Secondary", "Centered"].includes(layout) && (
        <CenteredHero
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={elRef}
          preheading={preheadingProp}
          heading={headingProp}
          headingMonoline={headingFont === "mbg Monoline"}
          subheading={subheading && subheading.childMarkdownRemark.html}
          anchorTarget={targetNavigationAnchor && `#${targetNavigationAnchor.anchorId}`}
          minHeight={minimumHeight}
          countdownDate={countdownTimerEndDate}
          backgroundImages={backgroundImages}
          metrics={metrics}
          buttonElement={button && <ContentfulButton component={button} />}
          secondaryButtonElement={
            secondaryButton && <ContentfulButton component={secondaryButton} />
          }
          alternate={layout === "Centered"}
          hasOverlay={includeColorOverlay}
          signupForm={
            signupForm && <ContentfulSignupForm component={signupForm} elRef={formRef} nested />
          }
        />
      )}
      {["Split", "Split Text-Centered"].includes(layout) && (
        <TextAndImage
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={elRef}
          preheading={preheadingProp}
          heading={headingProp}
          body={subheading && subheading.childMarkdownRemark.html}
          backgroundImages={backgroundImages}
          mediaElement={
            (foregroundImage && (
              <ContentfulImage
                component={{ image: foregroundImage, minHeight: minimumHeight }}
                onTop={onTop}
                nested
              />
            )) ||
            (foregroundVideo && (
              <ContentfulVideo component={foregroundVideo} onTop={onTop} nested />
            ))
          }
          variantRight={!!foregroundVideo}
          minHeight={minimumHeight}
          variantLeft={layout === "Split Text-Centered" ? "Hero-Centered" : "Hero"}
          buttonElement={button && <ContentfulButton component={button} />}
          secondaryButtonElement={
            secondaryButton && <ContentfulButton component={secondaryButton} />
          }
          signupForm={
            signupForm && <ContentfulSignupForm component={signupForm} elRef={formRef} nested />
          }
          hasOverlay={includeColorOverlay}
        />
      )}
    </ThemeProvider>
  )
}

export default ContentfulHero
